/* Admin: product photo library — browse, replace or delete saved product photos (reused for partner requests). */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ImagePlus, Loader2, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Input } from "@/components/ui/input";
import { AuthImg } from "@/components/PhotoRequestsList";

const H = () => ({ headers: getAuthHeader() });
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }) : "");

const Uploader = ({ product, label, tid, onDone, needName }) => {
  const ref = useRef(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async (files) => {
    const p = needName ? name : product;
    if (!files?.length || !p.trim()) { if (needName) toast.error("Write the product name first"); return; }
    if (!needName && !window.confirm(`Replace the saved photos of ${product} with ${files.length} new photo(s)?`)) return;
    setBusy(true);
    const fd = new FormData(); fd.append("product", p); Array.from(files).slice(0, 5).forEach(f => fd.append("files", f));
    try { await axios.post(`${API}/order-register/packer/library`, fd, H()); toast.success(`${p.toUpperCase()} saved in library`); setName(""); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false); if (ref.current) ref.current.value = "";
  };
  return (
    <span className="inline-flex items-center gap-1.5">
      {needName && <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New product name" className="h-8 w-48 text-xs" data-testid="lib-new-name" />}
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={(e) => go(e.target.files)} data-testid={`${tid}-input`} />
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={tid}
        className="inline-flex h-8 items-center gap-1 rounded-lg border bg-white px-2 text-xs font-semibold text-teal-800 hover:bg-teal-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : needName ? <Plus className="h-3.5 w-3.5" /> : <RefreshCw className="h-3.5 w-3.5" />} {label}</button>
    </span>
  );
};

const LibRow = ({ r, reload }) => {
  const drop = async (fid) => { await axios.delete(`${API}/order-register/packer/library/${r.key}/files/${fid}`, H()).catch(() => toast.error("Could not remove")); reload(); };
  const del = async () => {
    if (!window.confirm(`Delete ${r.product} from the photo library? Photos already sent to partners stay with them.`)) return;
    await axios.delete(`${API}/order-register/packer/library/${r.key}`, H()).catch(() => toast.error("Delete failed")); reload();
  };
  return (
    <div className="space-y-2 rounded-xl border bg-white p-3" data-testid={`lib-row-${r.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-sm">{r.product}</b><span className="text-[11px] text-slate-500">{r.files?.length || 0} photo(s) · {r.by_name} · {when(r.updated_at)}</span>
        <span className="ml-auto flex gap-1.5"><Uploader product={r.product} label="Replace" tid={`lib-replace-${r.key}`} onDone={reload} />
          <button type="button" onClick={del} className="inline-flex h-8 items-center rounded-lg border px-2 text-rose-600 hover:bg-rose-50" title="Delete from library" data-testid={`lib-del-${r.key}`}><Trash2 className="h-3.5 w-3.5" /></button></span>
      </div>
      <div className="flex flex-wrap gap-1.5">{(r.files || []).map(f => (
        <div key={f.id} className="relative">
          <AuthImg src={`${API}/order-register/packer/photo-request-files/${f.id}`} alt={f.filename} className="h-20 w-20 rounded-lg object-cover" tid={`lib-img-${f.id}`} />
          <button type="button" onClick={() => drop(f.id)} className="absolute right-0.5 top-0.5 rounded-full bg-white p-0.5 shadow" title="Remove this photo" data-testid={`lib-drop-${f.id}`}><X className="h-3 w-3" /></button>
        </div>))}</div>
    </div>
  );
};

export const PhotoLibraryManager = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => axios.get(`${API}/order-register/packer/library`, { ...H(), params: { q } })
    .then(r => setRows(r.data.rows)).catch(() => setRows([])), [q]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);
  return (
    <div className="space-y-3" data-testid="photo-library-manager">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product" className="h-8 w-56 pl-7 text-xs" data-testid="lib-search" /></div>
        <span className="ml-auto"><Uploader needName label="Add product" tid="lib-add" onDone={load} /></span>
      </div>
      <p className="flex items-center gap-1 text-[11px] text-slate-500"><ImagePlus className="h-3.5 w-3.5" /> Partners asking for one of these products get the saved photos instantly.</p>
      {rows === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin" />
        : !rows.length ? <p className="py-6 text-center text-sm text-slate-400" data-testid="lib-empty">No saved product photos yet</p>
          : rows.map(r => <LibRow key={r.key} r={r} reload={load} />)}
    </div>
  );
};
